import LogRocket from "logrocket";
import { analytics, firebase } from "../firebase/config";
import { fetchAllJobsEquipmentHidableColumnsDone } from "./equipmentOrders";
import { finishLoading, startLoading } from "./progress";
import {
  UPDATE_ALL_COMPANY_USERS,
  UPDATE_COMPANY_LEVEL_OPTIONS,
  UPDATE_USER_DETAILS,
  VERIFY_FAILURE,
  VERIFY_SUCCESS,
} from "./types";
import { uploadFileAndReturnDownloadableUrl } from ".";
import { BASE_API_URL, promiseNull } from "../constants/allConstants";
import axios from "axios";

// Note: not all functions below are "actions." Using this file to also separate
// firebase calls from UI logic.

const verifySuccess = (user) => {
  return {
    type: VERIFY_SUCCESS,
    user,
  };
};

const verifyFailure = () => {
  return {
    type: VERIFY_FAILURE,
  };
};
const updateAllCompanyUsers = (users) => {
  return {
    type: UPDATE_ALL_COMPANY_USERS,
    companyUsers: [...users],
  };
};

const updateUserDetails = (user) => {
  return {
    type: UPDATE_USER_DETAILS,
    user,
  };
};

const updateCompanyLevelOptions = (
  manufacturers,
  storageLocations,
  categories
) => {
  return {
    type: UPDATE_COMPANY_LEVEL_OPTIONS,
    manufacturers: [...manufacturers],
    storageLocations: [...storageLocations],
    categories: [...categories],
  };
};

export const loginUser = (email, password) => {
  return firebase.auth().signInWithEmailAndPassword(email, password);
};

export const signUpUser = (firstName, lastName, email, password) => {
  return firebase
    .auth()
    .createUserWithEmailAndPassword(email, password)
    .then((response) => {
      const uid = response.user.uid;
      const data = {
        id: uid,
        email,
        firstName,
        lastName,
        company: "No company",
        position: "",
        phoneNumber: "",
      };
      const usersRef = firebase.firestore().collection("users");
      return usersRef
        .doc(uid)
        .set(data)
        .then((snapshot) => {});
    });
};

export const getUser = () => () => {
  const usersRef = firebase.firestore().collection("users");
  return usersRef.data;
};

// For persistent login & every time user logins/logouts
export const verifyAuth = () => (dispatch) => {
  const usersRef = firebase.firestore().collection("users");
  return firebase.auth().onAuthStateChanged((user) => {
    // return listener for cleanup
    if (user) {
      user.getIdToken().then((is) => {
        console.log(is);
      });

      usersRef
        .doc(user.uid)
        .get()
        .then(async (document) => {
          let userData = document.data();
          analytics.setUserId(user.uid);
          analytics.setUserProperties({ company: userData.company });

          const env = process.env.NODE_ENV;
          const deploy_env = process.env.REACT_APP_DEPLOY_ENV;
          if (env === "production" && deploy_env === "prod") {
            LogRocket.init("sitetrace/sitetrace");
            LogRocket.identify(user.uid, {
              name: userData.firstName + " " + userData.lastName,
              email: userData.email,
              company: userData.company,
            });
          }
          dispatch(fetchCompanyLevelOptions(userData));

          const companyDetails = await firebase
            .firestore()
            .collection("companies")
            .doc(userData.company)
            .get();
          if (companyDetails.exists) {
            userData = {
              ...userData,
              companyDetails: {
                name: companyDetails.data().name,
                id: companyDetails.data().id,
              },
            };
          }
          dispatch(verifySuccess(userData));
        })
        .catch((error) => {
          dispatch(verifyFailure());
        });
    } else {
      dispatch(verifyFailure());
    }
  });
};

export const logoutUser = () => {
  return firebase.auth().signOut();
};
export const resetPassword = (email) => {
  let authDomain = "https://";
  const env = process.env.NODE_ENV;
  const deploy_env = process.env.REACT_APP_DEPLOY_ENV;
  if (env === "production" && deploy_env === "prod") {
    authDomain += "sitetrace-prod.firebaseapp.com";
  } else {
    authDomain += "sitetrace-dev.firebaseapp.com";
  }
  const actionCodeSettings = {
    url: authDomain,
    handleCodeInApp: false,
  };
  return firebase.auth().sendPasswordResetEmail(email, actionCodeSettings);
};

export const fetchAllUsers = (companyId) => (dispatch) => {
  dispatch(startLoading());
  return firebase
    .firestore()
    .collection("users")
    .where("company", "==", companyId)
    .get()
    .then((ref) => {
      let users = [];
      ref.forEach((r) => {
        const user = r.data();
        if (user.name || user.phoneNumber || user.email) {
          users.push(user);
        }
      });
      dispatch(updateAllCompanyUsers(users));
      dispatch(finishLoading());
    });
};

export const fetchCompanyLevelOptions = (user) => (dispatch) => {
  dispatch(startLoading("fetchCompanyLevelOptions"));
  firebase
    .firestore()
    .collection("companies")
    .doc(user.company)
    .get()
    .then(async (companyRef) => {
      const { manufacturers, storageLocations, categories } = companyRef.data();
      const allTools = [];
      const sortedStorageLocations = storageLocations.sort((a, b) => {
        if (a < b) {
          return -1;
        }
        if (a > b) {
          return 1;
        }
        return 0;
      });
      let finalCategories = [];
      const filteredCategories = categories.filter(
        (cat) => cat && cat.toLowerCase() !== "other"
      );
      if (filteredCategories.length !== categories.length) {
        finalCategories = [...filteredCategories, "Other"];
      } else {
        finalCategories = [...categories];
      }
      dispatch(
        updateCompanyLevelOptions(
          manufacturers,
          sortedStorageLocations,
          finalCategories
        )
      );
      dispatch(finishLoading("fetchCompanyLevelOptions"));
    });
};

export const updateProfilePicture = (picture, user) => async (dispatch) => {
  dispatch(startLoading("updateProfilePicture"));
  const storageRef = firebase.storage().ref();
  const imagesPath = "companies/" + user.company + "/profileImages/";
  let pictureUploadedDetails = null;
  if (picture && !picture.url) {
    // upload new photo. new photo has no url
    const details = await uploadFileAndReturnDownloadableUrl(
      imagesPath,
      storageRef,
      picture
    );
    pictureUploadedDetails = details;
  }
  if (user.profileImage && user.profileImage.url) {
    await firebase.storage().refFromURL(user.profileImage.url).delete();
  }
  await firebase
    .firestore()
    .collection("users")
    .doc(user.id)
    .update({ profileImage: pictureUploadedDetails });

  const userDetails = await firebase
    .firestore()
    .collection("users")
    .doc(user.id)
    .get()
    .catch(promiseNull);
  if (userDetails) {
    dispatch(verifySuccess({ ...user, ...userDetails.data() }));
  }

  dispatch(finishLoading("updateProfilePicture"));
};

export const reFetchUserDetails = (user) => (dispatch) => {
  dispatch(startLoading("reFetchUserDetails"));
  firebase
    .firestore()
    .collection("users")
    .doc(user.id)
    .get()
    .then(async (userRef) => {
      const userFetched = userRef.data();
      dispatch(updateUserDetails({ ...user, ...userFetched }));
      dispatch(
        fetchAllJobsEquipmentHidableColumnsDone(
          userFetched.allJobsEquipmentHiddenColumns
            ? userFetched.allJobsEquipmentHiddenColumns
            : []
        )
      );
      dispatch(finishLoading("reFetchUserDetails"));
    });
};
